import React, { useState } from "react";
import { Button, Alert, ProgressBar } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { DiagnosticQuestion } from "../components/DiagnosticQuestion";
import { useDiagnosticQuestions, useDiagnosticSubmit } from "../hooks/useDiagnostic";

/**
 * Step-by-step diagnostic questionnaire.
 * On mobile shows one question per screen (step state).
 * On submit, displays the suggested level and a CTA to view the calendar.
 */
function DiagnosticPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  const {
    data: questions,
    isLoading,
    isError,
  } = useDiagnosticQuestions();

  const submitMutation = useDiagnosticSubmit();

  if (isLoading) {
    return (
      <div className="text-center py-5" data-testid="loading">
        Cargando diagnóstico…
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="danger" className="m-3">
        Error al cargar el diagnóstico. Intenta de nuevo.
      </Alert>
    );
  }

  if (!questions || questions.length === 0) {
    return (
      <Alert variant="info" className="m-3">
        No hay preguntas disponibles en este momento.
      </Alert>
    );
  }

  if (result) {
    return (
      <div className="container-fluid py-4">
        <div className="row justify-content-center">
          <div className="col-12 col-sm-8 col-md-6">
            <div className="card text-center">
              <div className="card-body py-5">
                <h4 className="card-title">¡Diagnóstico completado!</h4>
                <p className="text-muted">Nivel sugerido</p>
                <div
                  className="fs-1 fw-bold text-primary my-3"
                  data-testid="suggested-level"
                >
                  {result.suggested_level}
                </div>
                <p className="text-muted small">Puntuación: {result.score}</p>
                <Button
                  variant="primary"
                  className="mt-3"
                  style={{ minHeight: 44 }}
                  onClick={() =>
                    navigate(`/${slug}/app/mi-espacio/calendario`)
                  }
                >
                  Ver calendario
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const current = questions[step];
  const total = questions.length;
  const pct = Math.round((step / total) * 100);
  const isLast = step === total - 1;

  async function handleSubmit() {
    const payload = Object.entries(answers).map(([qId, optId]) => ({
      question: parseInt(qId, 10),
      answer: optId,
    }));
    try {
      const data = await submitMutation.mutateAsync(payload);
      setResult(data);
    } catch {
      // submitMutation.isError will be true — shown below
    }
  }

  return (
    <div className="container-fluid py-4">
      <div className="row justify-content-center">
        <div className="col-12 col-sm-8 col-md-6">
          <h4 className="mb-1">Diagnóstico de nivel</h4>
          <p className="text-muted small mb-3">
            Responde cada pregunta para recibir tu nivel sugerido
          </p>

          <ProgressBar now={pct} className="mb-4" aria-label="Progreso del diagnóstico" />

          {submitMutation.isError && (
            <Alert variant="danger" className="mb-3">
              Error al enviar el diagnóstico. Intenta de nuevo.
            </Alert>
          )}

          <DiagnosticQuestion
            question={current}
            questionNumber={step + 1}
            total={total}
            selectedAnswer={answers[current.id]}
            onSelect={(optId) =>
              setAnswers((prev) => ({ ...prev, [current.id]: optId }))
            }
          />

          <div className="d-flex justify-content-between mt-3 gap-2">
            <Button
              variant="outline-secondary"
              disabled={step === 0}
              style={{ minHeight: 44 }}
              onClick={() => setStep((s) => s - 1)}
            >
              Anterior
            </Button>

            {isLast ? (
              <Button
                variant="success"
                disabled={!answers[current.id] || submitMutation.isPending}
                style={{ minHeight: 44 }}
                onClick={handleSubmit}
              >
                {submitMutation.isPending ? "Enviando…" : "Ver resultado"}
              </Button>
            ) : (
              <Button
                variant="primary"
                disabled={!answers[current.id]}
                style={{ minHeight: 44 }}
                onClick={() => setStep((s) => s + 1)}
              >
                Siguiente
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export { DiagnosticPage };
